// SPDX-License-Identifier: Apache-2.0

import React, { useContext, useEffect, useState } from "react";
import * as fa from "react-icons/fa";
import Modal from "react-bootstrap/Modal";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { Trans, useTranslation } from "react-i18next";
import {
  ChainNames,
  contractAddresses,
  discordLink,
  ethNetworkName,
  feedbackEmail,
  getExplorerURL,
} from "../constants/Constants";
import { ckbNetworkName } from "../constants/CKBNetwork";
import { AppContext } from "../AppContext";
import { InstructionsBody } from "../events/Notice";
import StellarAsset, { AssetID } from "../api/types/stellarAsset";
import EthereumAsset from "../api/types/ethereumAsset";
import { ModalBody, ModalHeader } from "react-bootstrap";
import { NervosAsset } from "../api/types/nervosAsset";
import ModalCloseButton from "./dialogs/ModalCloseButton";

/**
 * Displays a help circle button that when clicked opens a modal with a FAQ and
 * contact information.
 */
export function HelpMenu() {
  const { t } = useTranslation();
  const [showHelp, setshowHelp] = React.useState(false);
  const [assets, setAssets] = useState(
    new Map<AssetID, EthereumAsset | StellarAsset | NervosAsset>(),
  );
  const ctx = useContext(AppContext);

  useEffect(() => {
    if (!assets.size && ctx.client?.initialized) {
      const assetMap = new Map<
        string,
        EthereumAsset | StellarAsset | NervosAsset
      >();
      ctx.client.getAssets({ type: "all" }).then((aRsp) => {
        if (aRsp.ethereumAssets) {
          aRsp.ethereumAssets.map((a) => assetMap.set(a.assetID(), a));
        } else if (aRsp.stellarAssets) {
          aRsp.stellarAssets.map((a) => assetMap.set(a.assetID(), a));
        } else if (aRsp.nervosAssets) {
          aRsp.nervosAssets.map((a) => assetMap.set(a.assetID(), a));
        }
        setAssets(assetMap);
      });
    }
  }, [assets, ctx.client?.initialized]);

  const shortenAddress = (address: string) => {
    if (!address || address.length <= 10) return address;
    return `${address.substring(0, 5)}...${address.substring(
      address.length - 4,
    )}`;
  };

  const getAssetList = (): JSX.Element => {
    if (!ctx.client?.initialized) {
      return <p>{t("A_tokenInfoNotConnected")}</p>;
    } else {
      return (
        <>
          <ul>
            {Array.from(assets.values()).map((token) => (
              <>
                {token instanceof StellarAsset ? (
                  <li key={token.assetID()}>
                    <Trans
                      i18nKey={
                        token.code === "XLM"
                          ? "A_tokenInfoB"
                          : token.code === "USDC"
                            ? "A_tokenInfoC"
                            : "A_tokenInfoA"
                      }
                      values={{
                        tokenName: token.name,
                        chainName: ChainNames.get(token.chainID),
                        address: shortenAddress(
                          contractAddresses.get(token.contractID)!,
                        ),
                      }}
                    >
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={getExplorerURL(
                          token.chainID,
                          token.contractID,
                          "token",
                        )}
                        style={{ color: "#00C9B8" }}
                      ></a>
                    </Trans>
                  </li>
                ) : (
                  <>
                    {token instanceof EthereumAsset ? (
                      <li key={token.assetID()}>
                        <Trans
                          i18nKey="A_tokenInfo"
                          values={{
                            tokenName: token.name,
                            chainName: ChainNames.get(token.chainID),
                            address: token.address.toString().substr(0, 8),
                          }}
                        >
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={getExplorerURL(
                              token.chainID,
                              token.address,
                              "token",
                            )}
                            style={{ color: "#00C9B8" }}
                          ></a>
                        </Trans>
                      </li>
                    ) : (
                      <li key={token.assetID()}>
                        <Trans
                          i18nKey="A_tokenInfo"
                          values={{
                            tokenName: token.name,
                            chainName: ChainNames.get(token.chainID),
                            address: token.address.toString().substr(0, 8),
                          }}
                        >
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={getExplorerURL(
                              token.chainID,
                              token.address.codeHash,
                              "token",
                            )}
                            style={{ color: "#00C9B8" }}
                          ></a>
                        </Trans>
                      </li>
                    )}
                  </>
                )}
              </>
            ))}
          </ul>
        </>
      );
    }
  };

  return (
    <>
      <button className="HelpCircle" onClick={() => setshowHelp(true)}>
        <fa.FaQuestion />
      </button>
      <Modal
        className="modal-dialog-centered"
        id={"help-modal"}
        aria-labelledby="contained-modal-title-vcenter"
        show={showHelp}
        onHide={() => setshowHelp(false)}
        centered
      >
        <ModalHeader>
          <Modal.Title id="contained-modal-title-vcenter">
            {t("help")}
          </Modal.Title>
          <ModalCloseButton onClick={() => setshowHelp(false)} />
        </ModalHeader>
        <ModalBody>
          <h5>{t("FAQ")}</h5>
          <Accordion allowZeroExpanded={true}>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>{"Instructions"}</AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <InstructionsBody />
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>
                  {t("Q_proposalStuck")}
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>{t("A_proposalStuck")}</p>
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>
                  {t("Q_fundingFailed")}
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>{t("A_fundingFailed")}</p>
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>{t("Q_settleFailed")}</AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>{t("A_settleFailed")}</p>
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>
                  {t("Q_recoverInterrupted")}
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>{t("A_recoverInterrupted")}</p>
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>
                  {t("Q_accountSwitch")}
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>{t("A_accountSwitch")}</p>
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>{t("Q_tokensHidden")}</AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>{t("A_tokensHidden")}</p>
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>{t("Q_tokenInfo")}</AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>{getAssetList()}</AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>
                  {t("Q_walletSupport")}
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>{t("A_walletSupport")}</p>
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>{t("Q_fees")}</AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>{t("A_fees")}</p>
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>{t("Q_ckbUpFront")}</AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>{t("A_ckbUpFront")}</p>
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>{t("Q_maxPerSwap")}</AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>{t("A_maxPerSwap")}</p>
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>{t("Q_swapDuration")}</AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>{t("A_swapDuration")}</p>
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>{t("Q_Mainnet")}</AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>
                  {t("A_Mainnet", {
                    ethNetwork: ethNetworkName,
                    ckbNetwork: ckbNetworkName,
                  })}
                </p>
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>{t("Q_CrossChain")}</AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p style={{ display: "inline" }}>{t("A_CrossChain")}</p>
              </AccordionItemPanel>
            </AccordionItem>
          </Accordion>
          <hr />
          {/* We have to use the Trans component here, in order to
           interpolate links in our text.*/}
          <Trans i18nKey="contactInfo">
            {t("contactInfo")}
            <a href={`mailto:${feedbackEmail}`} style={{ color: "#00C9B8" }}>
              {{ feedbackEmail }}
            </a>
            .
          </Trans>
          <br />
          <Trans i18nKey={"discordInfo"}>
            {t("discordInfo")}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={discordLink}
              style={{ color: "#00C9B8" }}
            >
              Discord
            </a>
          </Trans>
        </ModalBody>
      </Modal>
    </>
  );
}
